import { useEffect, useState } from "react";

import {
  Box,
  Typography,
  Button,
  Alert,
  IconButton,
  Switch,
  Tooltip,
  Avatar,
  Divider,
  useMediaQuery,
} from "@mui/material";

import { useSpring, animated } from "@react-spring/web";
import LinearProgress from "@mui/material/LinearProgress";
import { lighten } from "@mui/material/styles";
import ShuffleIcon from "@mui/icons-material/Shuffle";
import { MathJaxContext, MathJax } from "better-react-mathjax";
import SpaceBarIcon from "@mui/icons-material/SpaceBar";
import LooksOneIcon from "@mui/icons-material/LooksOne";
import LooksTwoIcon from "@mui/icons-material/LooksTwo";
import CloseIcon from "@mui/icons-material/Close";
import Looks3Icon from "@mui/icons-material/Looks3";
import KeyboardDemo from "./KeyboardDemo";
import OpenDyslexic from "./OpenDyslexic";

import Looks4Icon from "@mui/icons-material/Looks4";
import FolderIcon from "@mui/icons-material/FolderOpen";

import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import LightbulbIcon from "@mui/icons-material/LightbulbOutlined";
import LooksThreeIcon from "@mui/icons-material/Looks3";
import LooksFourIcon from "@mui/icons-material/Looks4";
import GenerateIcon from "@mui/icons-material/AutoAwesome";
import BookmarkIcon from "@mui/icons-material/BookmarkBorder";
import CheckCircleIcon from "@mui/icons-material/Check";

import FlashcardsBank from "./FlashcardsBank";
import Voice from "./Voice";
import AlertKeyboardShortcuts from "./AlertKeyboardShortcuts";
import Hint from "./Hint";
import DashboardIcon from "@mui/icons-material/Dashboard";
import AIDialog from "./AIDialog";
import { formatDate, fixLatexNotation } from "./functions";
import RecommendationComponent from "./RecommendationComponent";
import { useNavigate } from "react-router-dom";

const FlashcardGame = ({
  currentCollection,
  currentCardIndex,
  showBack,
  sessionSummary,
  bookmarks,
  creatorInfo,
  originalCollectionId,
  user,
  theme,
  styles,
  isDark,
  filterFlashcards,
  onFlipCard,
  onShuffleCards,
  onToggleBookmark,
  onRateCard,
  onAskAI,
  aiDialogOpen,
  setAiDialogOpen,
  aiClueDialogOpen,
  setAiClueDialogOpen,
  showCreateView,
  setCurrentCollection,
  setIsCreateViewVisible,
}) => {
  const { transform, opacity } = useSpring({
    opacity: showBack ? 1 : 0,
    transform: `perspective(800px) rotateY(${showBack ? 180 : 0}deg)`,
    config: { mass: 5, tension: 500, friction: 80 },
  });
  const navigate = useNavigate();
  const [animateButtonIndex, setAnimateButtonIndex] = useState(-1);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [openFlashcardsBank, setOpenFlashcardsBank] = useState(false);

  useEffect(() => {
    // Only run if the card is flipped to back and first time viewing
    if (showBack && !localStorage.getItem("hasSeenButtons515")) {
      // Track how many times we've run the animation
      let animationRuns = 0;
      const maxRuns = 2; // Run the animation twice

      // Function to run a single animation sequence
      const runAnimation = () => {
        let currentButton = 0;
        // Start with the first button
        setAnimateButtonIndex(0);

        // Create interval to cycle through buttons
        const animationTimer = setInterval(() => {
          currentButton++;
          // If we've animated all buttons in this run
          if (currentButton > 3) {
            clearInterval(animationTimer);
            animationRuns++;

            // If we've completed all runs, finish
            if (animationRuns >= maxRuns) {
              setAnimateButtonIndex(-1);
              localStorage.setItem("hasSeenButtons515", "true");
            } else {
              // Start the next run after a small pause
              setTimeout(() => runAnimation(), 500);
            }
            return;
          }
          setAnimateButtonIndex(currentButton);
        }, 300); // Each button gets highlighted for 300ms
      };

      // Add initial delay before starting the first animation
      const initialDelay = setTimeout(() => {
        runAnimation();
      }, 500);

      return () => clearTimeout(initialDelay);
    }
  }, [showBack]);

  const handleAskAIForClue = () => {
    setAiClueDialogOpen(true);
  };

  const handleAskAI = () => {
    setAiDialogOpen(true);
    if (onAskAI) onAskAI();
  };

  return (
    <>
      <MathJaxContext
        config={{
          tex: {
            inlineMath: [
              ["$", "$"],
              ["\\(", "\\)"],
            ],
            displayMath: [
              ["$$", "$$"],
              ["\\[", "\\]"],
            ],
          },
          chtml: {
            scale: 1.3,
            fontFamily: "Arial",
            matchFontHeight: false,
            displayAlign: "center", // Changed from "left" to "center"
            displayIndent: "0",

            container: { width: "100%" }, // Add container width constraint
            styles: {
              ".MathJax": {
                overflow: "hidden", // Prevent MathJax from overflowing
                maxWidth: "100%", // Constrain maximum width
                wordBreak: "break-word", // Allow breaking formulas if needed
                margin: "0 auto",
              },
            },
          },
        }}
      >
        <IconButton
          sx={{
            ml: { xs: 0, md: 2 },
            position: "absolute",
            top: { xs: 10, md: 20 },
            left: 5,
          }}
          onClick={(e) => {
            setCurrentCollection(null);
            setIsCreateViewVisible(true);
            navigate("/app/flashcards");
          }}
        >
          <CloseIcon
            sx={{
              fontSize: { xs: "1.5rem", md: "2rem" },
              color: "text.secondary",
            }}
          />
        </IconButton>

        <Box
          sx={{
            position: "absolute",
            top: { xs: 10, md: 23 },
            right: 115,
            zIndex: 10,
          }}
        >
          <OpenDyslexic />
        </Box>

        <Box
          sx={{
            width: { xs: "100%", md: "70%" },
            display: "block",
            margin: "0 auto",
            paddingTop: "50px !important",
          }}
        >
          <AIDialog
            userId={user.uid}
            open={aiDialogOpen}
            onClose={() => setAiDialogOpen(false)}
            initialQuestion={
              currentCollection?.flashcards?.[currentCardIndex]
                ? `${currentCollection.flashcards[currentCardIndex].front} - ${currentCollection.flashcards[currentCardIndex].back}
Please explain these flashcards and help me understand the answer options. Skriv på svenska.`
                : "Please explain the current flashcard and help me understand the answer options. Skriv på svenska."
            }
            systemMessage="You are a helpful AI assistant designed to explain and assist flashcards practiceProvide clear, pedagogical explanations using analogies and examples when appropriate. Skriv på svenska, skriv kortfattat."
            theme={theme}
          />

          <AIDialog
            userId={user.uid}
            open={aiClueDialogOpen}
            onClose={() => setAiClueDialogOpen(false)}
            initialQuestion={
              currentCollection?.flashcards?.[currentCardIndex]
                ? `${currentCollection.flashcards[currentCardIndex].front} - ${currentCollection.flashcards[currentCardIndex].back}
Please explain these flashcards and help me understand the answer options. Skriv på svenska.`
                : "Please explain the current flashcard and help me understand the answer options. Skriv på svenska."
            }
            systemMessage="You are a helpful AI assistant designed to give a hint to the user in order to understand the flashcard. Provide a short and simple hint so the user can learn to think for themseleves. If they don't manage then give them another hint and so on. Skriv på svenska, skriv kortfattat."
            theme={theme}
          />

          <Box sx={{ mt: { xs: 0, md: 5 } }}>
            <Typography
              variant="h5"
              sx={{
                ...styles.headerAboveBox,
                textAlign: "center",
                display: "block",
                fontWeight: "600",
                mb: 2,
              }}
            >
              {currentCollection.collectionName}
            </Typography>
          </Box>
          <Box
            sx={{
              width: { xs: "100%", md: "90%" },
              height: { xs: "420px", md: "400px" }, // Increased height for mobile
              minHeight: { xs: "420px", md: "400px" }, // Increased min-height for mobile

              position: "relative",
              margin: "0 auto",
              mt: 2,
            }}
          >
            <LinearProgress
              variant="determinate"
              value={
                (currentCardIndex /
                  filterFlashcards(currentCollection).length) *
                100
              }
              sx={{
                height: 7,
                borderRadius: 5,
                marginBottom: 2,
                bgcolor: (theme) => lighten(theme.palette.text.main, 0.7),
                "& .MuiLinearProgress-bar": {
                  borderRadius: 5,
                  backgroundColor: (theme) => theme.palette.text.main,
                  transition: "transform 0.5s ease-in-out",
                },
              }}
            />

            {/* Front of card */}
            <animated.div
              style={{
                position: "absolute",
                width: "100%",
                height: "100%",
                backfaceVisibility: "hidden",
                opacity: opacity.to((o) => 1 - o),
                transform,
                cursor: "pointer",
              }}
              onClick={onFlipCard}
            >
              {/* Card controls */}
              <Box sx={{ position: "absolute", top: 10, right: 10, zIndex: 1 }}>
                <Tooltip title="Blanda om">
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      onShuffleCards();
                    }}
                    sx={{ color: theme.palette.text.primary }}
                  >
                    <ShuffleIcon sx={{ color: "text.secondary" }} />
                  </IconButton>
                </Tooltip>
              </Box>

              <Box sx={{ position: "absolute", top: 10, right: 50, zIndex: 1 }}>
                <Voice
                  content={
                    filterFlashcards(currentCollection)[currentCardIndex]?.front
                  }
                  fontSize={26}
                  userId={user.uid}
                />
              </Box>

              <Box sx={{ position: "absolute", top: 10, left: 10, zIndex: 1 }}>
                <Tooltip
                  title={bookmarks[currentCardIndex] ? "Sparat" : "Spara"}
                >
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      onToggleBookmark();
                    }}
                  >
                    {bookmarks[currentCardIndex] ? (
                      <CheckCircleIcon />
                    ) : (
                      <BookmarkIcon sx={{ color: "text.secondary" }} />
                    )}
                  </IconButton>
                </Tooltip>
              </Box>

              <Box
                sx={{
                  position: "absolute",
                  top: "20px",
                  left: "60px",
                  zIndex: 1,
                  display: "none",
                }}
              >
                <Hint
                  front={
                    filterFlashcards(currentCollection)[currentCardIndex]?.front
                  }
                  back={
                    filterFlashcards(currentCollection)[currentCardIndex]?.back
                  }
                  user={user}
                  theme={theme}
                />
              </Box>

              {/* Card content */}
              <Typography
                className="card-text"
                variant="h6"
                sx={{
                  ...styles.cardText,

                  fontSize: {
                    xs: "17px",
                    md: "22px",
                  },
                  overflowY: "auto",
                  maxHeight: "100%",
                  wordBreak: "break-word",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {filterFlashcards(currentCollection)[currentCardIndex]
                  ?.imageUrlFront ? (
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                      gap: 2,
                      width: "100%",
                      alignItems: "center",
                    }}
                  >
                    <Box sx={{ textAlign: "center" }}>
                      <MathJax dynamic>
                        {fixLatexNotation(
                          filterFlashcards(currentCollection)[currentCardIndex]
                            ?.front || "Inga flashcards tillgängliga",
                        )}
                      </MathJax>
                    </Box>
                    <Box sx={{ display: "flex", justifyContent: "center" }}>
                      <img
                        src={
                          filterFlashcards(currentCollection)[currentCardIndex]
                            ?.imageUrlFront
                        }
                        alt="Card visualization"
                        style={{
                          maxWidth: "100%",
                          maxHeight: "250px",
                          objectFit: "contain",
                          borderRadius: "10px",
                        }}
                      />
                    </Box>
                  </Box>
                ) : (
                  <>
                    <MathJax dynamic>
                      {fixLatexNotation(
                        filterFlashcards(currentCollection)[currentCardIndex]
                          ?.front || "Inga flashcards tillgängliga",
                      )}
                    </MathJax>
                  </>
                )}
              </Typography>
            </animated.div>

            {/* Back of card */}
            {/* Back of card */}
            <animated.div
              style={{
                position: "absolute",
                width: "100%",
                height: "100%",
                backfaceVisibility: "hidden",
                opacity,
                transform: transform.to((t) => `${t} rotateY(180deg)`),
                cursor: "pointer",
              }}
              onClick={onFlipCard}
            >
              {/* Card content container */}
              <Box
                className="card-text"
                sx={{
                  ...styles.cardText,
                  display: "flex",
                  flexDirection: "column",
                  height: "100%",
                  position: "relative",
                }}
              >
                {/* Main content area */}
                <Box
                  sx={{
                    flex: 1,
                    overflowY: "auto",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "0px",
                    paddingBottom: { xs: "120px", md: "90px" },
                    maxHeight: "100%", // Ensure it doesn't exceed the card height
                    height: "100%", // Take full height of the parent
                  }}
                >
                  <Typography
                    className="card-text"
                    variant="h6"
                    sx={{
                      fontSize: {
                        xs: "17px",
                        md: "22px",
                      },
                      wordBreak: "break-word",
                      width: "100%",

                      textAlign: "center",
                      overflowY: "auto", // Add scroll to typography element
                      maxHeight: "100%", // Allow typography to use full height
                      overflowX: "hidden",
                    }}
                  >
                    {filterFlashcards(currentCollection)[currentCardIndex]
                      ?.imageUrlBack ? (
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                          gap: 2,
                          width: "100%",
                          alignItems: "center",
                        }}
                      >
                        <Box className="card-text" sx={{ textAlign: "center" }}>
                          <MathJax dynamic>
                            <span>
                              {fixLatexNotation(
                                filterFlashcards(currentCollection)[
                                  currentCardIndex
                                ]?.back || "Inga flashcards tillgängliga",
                              )}
                            </span>
                          </MathJax>
                        </Box>
                        <Box sx={{ display: "flex", justifyContent: "center" }}>
                          <img
                            src={
                              filterFlashcards(currentCollection)[
                                currentCardIndex
                              ]?.imageUrlBack
                            }
                            alt="Card visualization"
                            style={{
                              maxWidth: "100%",
                              maxHeight: "250px",
                              objectFit: "contain",
                              borderRadius: "10px",
                            }}
                          />
                        </Box>
                      </Box>
                    ) : (
                      <>
                        <MathJax dynamic>
                          <span>
                            {fixLatexNotation(
                              filterFlashcards(currentCollection)[
                                currentCardIndex
                              ]?.back || "Inga flashcards tillgängliga",
                            )}
                          </span>
                        </MathJax>
                      </>
                    )}
                  </Typography>
                </Box>
                {/* Difficulty buttons at bottom of card */}
                {/* Difficulty buttons at bottom of card */}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", md: "row" },
                    justifyContent: "space-between",
                    width: "100%",
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    right: 0,
                    padding: "15px",
                    borderRadius: 4,
                    backgroundColor: (theme) =>
                      theme.palette.mode === "dark"
                        ? "rgb(26 39 53)"
                        : "rgba(255,255,255,0.8)",
                  }}
                  onClick={(e) => e.stopPropagation()} // Prevents card flip when clicking buttons
                >
                  {/* First row on mobile */}
                  <Box
                    sx={{
                      display: "flex",
                      width: "100%",
                      mb: { xs: 1, md: 0 },
                    }}
                  >
                    <Button
                      variant="outlined"
                      onClick={() => onRateCard("fail")}
                      startIcon={isMobile ? null : <LooksOneIcon />}
                      sx={{
                        // transform:
                        //   animateButtonIndex === 0 ? "scale(1.04)" : "scale(1)",
                        transition:
                          "transform 0.3s ease, background-color 0.3s ease", // Add background-color transition
                        backgroundColor:
                          animateButtonIndex === 0
                            ? isDark
                              ? "rgba(239, 68, 68, 0.15)"
                              : "#FEE2E2"
                            : isDark
                              ? "#1d2e3d"
                              : "white",
                        flex: 1,
                        mx: 0.5,
                        border: "1px solid #F87171",
                        color: "#EF4444",
                        borderRadius: 3,
                        p: { xs: 1, md: 2 },
                        fontSize: { xs: "0.875rem", md: "1rem" },
                        textTransform: "none",
                        fontWeight: "500",
                        "&:hover": {
                          backgroundColor: isDark
                            ? "rgba(239, 68, 68, 0.15)"
                            : "#FEE2E2",
                          border: "1px solid #F87171",
                          color: "#EF4444",
                        },
                      }}
                    >
                      Väldigt svår
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={() => onRateCard("hard")}
                      startIcon={isMobile ? null : <LooksTwoIcon />}
                      sx={{
                        // transform:
                        //   animateButtonIndex === 1 ? "scale(1.04)" : "scale(1)",
                        transition:
                          "transform 0.3s ease, background-color 0.3s ease", // Add background-color transition
                        backgroundColor:
                          animateButtonIndex === 1
                            ? isDark
                              ? "rgba(239, 68, 68, 0.15)"
                              : "#FEE2E2"
                            : isDark
                              ? "#1d2e3d"
                              : "white",
                        flex: 1,
                        mx: 0.5,
                        border: "1px solid #FBBF24",
                        color: "#F59E0B",
                        borderRadius: 3,
                        p: { xs: 1, md: 2 },
                        fontSize: { xs: "0.875rem", md: "1rem" },
                        textTransform: "none",
                        fontWeight: "500",
                        "&:hover": {
                          backgroundColor: isDark
                            ? "rgba(245, 158, 11, 0.15)"
                            : "#FEF3C7",
                          border: "1px solid #FBBF24",
                          color: "#F59E0B",
                        },
                      }}
                    >
                      Svår
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={() => onRateCard("good")}
                      startIcon={isMobile ? null : <Looks3Icon />}
                      sx={{
                        // transform:
                        //   animateButtonIndex === 2 ? "scale(1.04)" : "scale(1)",
                        transition:
                          "transform 0.3s ease, background-color 0.3s ease", // Add background-color transition
                        flex: 1,
                        mx: 0.5,
                        border: "1px solid #60A5FA",
                        color: "#3B82F6",
                        borderRadius: 3,
                        p: { xs: 1, md: 2 },
                        fontSize: { xs: "0.875rem", md: "1rem" },
                        textTransform: "none",
                        fontWeight: "500",
                        backgroundColor:
                          animateButtonIndex === 2
                            ? isDark
                              ? "rgba(59, 130, 246, 0.15)"
                              : "#DBEAFE"
                            : isDark
                              ? "#1d2e3d"
                              : "white",
                        "&:hover": {
                          backgroundColor: isDark
                            ? "rgba(59, 130, 246, 0.15)"
                            : "#DBEAFE",
                          border: "1px solid #60A5FA",
                          color: "#3B82F6",
                        },
                      }}
                    >
                      Ganska lätt
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={() => onRateCard("easy")}
                      startIcon={isMobile ? null : <Looks4Icon />}
                      sx={{
                        // transform:
                        //   animateButtonIndex === 3 ? "scale(1.04)" : "scale(1)",
                        transition:
                          "transform 0.3s ease, background-color 0.3s ease", // Add background-color transition
                        flex: 1,
                        mx: 0.5,
                        border: "1px solid #4ADE80",
                        color: "#22C55E",
                        borderRadius: 3,
                        p: { xs: 1, md: 2 },
                        fontSize: { xs: "0.875rem", md: "1rem" },
                        textTransform: "none",
                        fontWeight: "500",
                        backgroundColor:
                          animateButtonIndex === 3
                            ? isDark
                              ? "rgba(34, 197, 94, 0.15)"
                              : "#DCFCE7"
                            : isDark
                              ? "#1d2e3d"
                              : "white",
                        "&:hover": {
                          backgroundColor: isDark
                            ? "rgba(34, 197, 94, 0.15)"
                            : "#DCFCE7",
                          border: "1px solid #4ADE80",
                          color: "#22C55E",
                        },
                      }}
                    >
                      Lätt
                    </Button>
                  </Box>
                </Box>{" "}
              </Box>
            </animated.div>
          </Box>

          {/* Controls */}

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              mt: { xs: 6, md: 10 },
            }}
          >
            {/* Flip card button */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                width: "100%",
                mb: 4,
              }}
            >
              <Button
                variant="purp"
                onClick={onFlipCard}
                endIcon={<SpaceBarIcon />}
                sx={{ width: { xs: "100%", md: "282px" } }}
              >
                Vänd kort
              </Button>
            </Box>
            <Divider sx={{ mt: 0 }} />
            {/* Ask AI button */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                width: "100%",
                flexDirection: { xs: "column", md: "row" },
                gap: 2,
              }}
            >
              <Button
                variant="print"
                onClick={handleAskAI}
                startIcon={<ChatBubbleOutlineIcon />}
                sx={{
                  width: { xs: "100%", md: "auto" },
                  mb: { xs: 2, md: 0 },
                }}
              >
                Fråga AI
              </Button>

              <Button
                variant="print"
                onClick={handleAskAIForClue}
                sx={{
                  width: { xs: "100%", md: "auto" },
                  mb: { xs: 2, md: 0 },
                }}
                startIcon={<LightbulbIcon />}
              >
                Ledtråd
              </Button>

              <Button
                variant="print"
                onClick={() => navigate("/app/mina-flashcards")}
                startIcon={<FolderIcon />}
                sx={{
                  width: { xs: "100%", md: "auto" },
                  maxWidth: { xs: "100%", md: "none" },
                  display: "none",
                }}
              >
                Mina flashcards
              </Button>
            </Box>
          </Box>

          {/* Additional info */}
          <Box sx={{ display: "none" }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                alignItems: { xs: "stretch", md: "flex-start" },
                justifyContent: "space-between",
                gap: 2,
                mt: 7,
                width: { xs: "100%", md: "100%" },
              }}
            >
              {creatorInfo && (
                <Box
                  sx={{
                    ...styles.smallBox,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor:
                      theme.palette.mode === "dark" ? "#2a3b4c57" : "#fff",
                    borderRadius: "10px",
                    padding: "10px",
                    marginTop: { xs: "30px", md: 0 },
                    width: { xs: "100%", md: "fit-content" },
                  }}
                >
                  <Avatar
                    src={creatorInfo.avatarUrl}
                    sx={{ width: 37, height: 37, mr: 2 }}
                  >
                    {creatorInfo.displayName[0].toUpperCase()}
                  </Avatar>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                      {creatorInfo.displayName}
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Typography
                        variant="body2"
                        sx={{
                          color: theme.palette.text.secondary,
                          fontSize: "0.8rem",
                        }}
                      >
                        {formatDate(currentCollection.createdAt)}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              )}

              <Box
                sx={{
                  ...styles.smallBox,
                  backgroundColor:
                    theme.palette.mode === "dark" ? "#2a3b4c57" : "#fff",
                  borderRadius: "10px",
                  width: { xs: "100%", md: "48%" },
                  padding: "0px",
                  margin: { xs: "0 auto", md: 0 },
                  marginTop: { xs: "80px", md: 0 },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    color: theme.palette.text.secondary,
                  }}
                >
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: "bold",
                      fontSize: "13px",
                      flexShrink: 0,
                      padding: "20px",
                      paddingRight: "0px",
                    }}
                  >
                    GENVÄGAR
                  </Typography>
                  <Divider orientation="vertical" flexItem sx={{ mx: 2 }} />
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: "2px",
                      ml: 0,
                    }}
                  >
                    <SpaceBarIcon />
                    <LooksOneIcon />
                    <LooksTwoIcon />
                    <LooksThreeIcon />
                    <LooksFourIcon />
                    <Typography variant="body2" sx={{ ml: 1 }}>
                      (F = Fråga AI)
                    </Typography>
                  </Box>
                </Box>
              </Box>

              <FlashcardsBank
                open={openFlashcardsBank}
                onClose={() => setOpenFlashcardsBank(false)}
                user={user}
                styles={styles}
                theme={theme}
              />
              <Box sx={{ width: { xs: "100%", md: "auto" } }}>
                <RecommendationComponent
                  itemId={originalCollectionId}
                  userId={user.uid}
                  collectionName="flashcardCollections"
                  questionText="Skulle du rekommendera dessa flashcards till andra användare?"
                  largerStyle={true}
                />
              </Box>
            </Box>
          </Box>
        </Box>
      </MathJaxContext>
    </>
  );
};

export default FlashcardGame;
